import { useRef, useState } from 'react';
import { ChevronsLeftRight } from 'lucide-react';

/** Draggable before/after comparison. `url` = after, `beforeUrl` = before. */
export default function BeforeAfter({
  beforeUrl,
  afterUrl,
  alt,
  className = '',
}: {
  beforeUrl: string;
  afterUrl: string;
  alt: string;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(50); // divider position, %
  const dragging = useRef(false);

  const moveTo = (clientX: number) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setPos(Math.min(98, Math.max(2, ((clientX - rect.left) / rect.width) * 100)));
  };

  return (
    <div
      ref={ref}
      className={`relative select-none overflow-hidden ${className}`}
      onPointerDown={(e) => {
        dragging.current = true;
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
        moveTo(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && moveTo(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
      style={{ touchAction: 'pan-y' }}
    >
      <img src={afterUrl} alt={`${alt} — after`} className="block w-full" draggable={false} />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={beforeUrl} alt={`${alt} — before`} className="block h-full w-full object-cover" draggable={false} />
      </div>
      {/* divider */}
      <div className="pointer-events-none absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -ml-px w-0.5 bg-white/90 shadow-[0_0_8px_rgba(0,0,0,0.6)]" />
        <div className="absolute top-1/2 -ml-[18px] -mt-[18px] flex h-9 w-9 items-center justify-center rounded-full border border-white/50 bg-night-950/80 text-white backdrop-blur">
          <ChevronsLeftRight size={17} />
        </div>
      </div>
      <span className="pointer-events-none absolute left-3 top-3 rounded-full bg-night-950/75 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white backdrop-blur">
        Before
      </span>
      <span className="pointer-events-none absolute right-3 top-3 rounded-full bg-accent-500/85 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white backdrop-blur">
        After
      </span>
    </div>
  );
}
